/**
 * Principal-scoped capability gate helpers backed by GET /auth/me.capability_catalog.
 *
 * Wave F2 anti-implant: catalog ``enabled`` is **server SoT** (env flags via
 * capability_catalog.py). FE may soft-hide chrome only — never invent Live /
 * never override from localStorage / DOM.
 */

import { workspaceBlockedHtml } from "./workspace-blocked.js";

/**
 * @param {object|null|undefined} me
 * @param {string} capabilityId
 * @returns {object|null}
 */
export function catalogEntry(me, capabilityId) {
  const rows = me && Array.isArray(me.capability_catalog) ? me.capability_catalog : [];
  const id = String(capabilityId || "");
  for (const row of rows) {
    if (row && String(row.id || "") === id) return row;
  }
  return null;
}

/**
 * @param {object|null|undefined} me
 * @param {string} capabilityId
 * @returns {boolean}
 */
export function catalogEnabled(me, capabilityId) {
  const row = catalogEntry(me, capabilityId);
  // Server boolean only — ignore any client-forged sibling fields.
  return !!(row && row.enabled === true);
}

/**
 * True when any listed capability id is enabled (nav OR semantics).
 * @param {object|null|undefined} me
 * @param {string[]|null|undefined} capabilityIds
 * @returns {boolean}
 */
export function catalogAnyEnabled(me, capabilityIds) {
  if (!Array.isArray(capabilityIds) || !capabilityIds.length) return false;
  for (const id of capabilityIds) {
    if (catalogEnabled(me, id)) return true;
  }
  return false;
}

/**
 * Soften env-style flags for operator copy (never show raw FLAG_NAMES).
 * @param {string} flag
 * @returns {string}
 */
export function humanizeEnvFlag(flag) {
  const raw = String(flag || "").trim();
  if (!raw) return "";
  const words = raw
    .replace(/^CROC_/i, "")
    .replace(/_ENABLED$/i, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Shared disabled-capability card for flag-off / unavailable pages.
 * Always includes Home + Settings escape paths (no dead-ends).
 * @param {{ title: string, flag?: string, detail?: string, backHref?: string, backLabel?: string, plainCard?: boolean }} opts
 */
export function capabilityUnavailableHtml(opts) {
  const title = String((opts && opts.title) || "Not available yet");
  const flagRaw = opts && opts.flag ? String(opts.flag).trim() : "";
  const humanLabel = humanizeEnvFlag(flagRaw);
  const detail = opts && opts.detail
    ? String(opts.detail)
    : humanLabel
      ? `${humanLabel} is not available for your account right now.`
      : "This tool is not available for your account right now.";
  const shell = workspaceBlockedHtml({
    title,
    detail,
    backHref: (opts && opts.backHref) || "#/settings",
    backLabel: (opts && opts.backLabel) || "Back to Settings",
  });
  if (opts && opts.plainCard) {
    // Extract inner card only for callers that embed into an existing shell.
    const m = shell.match(/<section[\s\S]*?<\/section>/);
    return m ? m[0] : shell;
  }
  return shell;
}

/** Alias for routes that need a generic blocked desk. */
export { workspaceBlockedHtml };
